import React from 'react';
import { Link } from 'react-router-dom';
import './MegaMenu.css';
import { Brain, Building2, Cog, Wrench, Cloud, Shield, HeartHandshake, RefreshCw, Zap, ClipboardList, Factory, Workflow } from 'lucide-react';

interface MegaMenuProps {
  type: 'services' | 'solutions' | 'products' | 'industries';
  isOpen: boolean;
  onClose: () => void;
}


export const MegaMenu: React.FC<MegaMenuProps> = ({ type, isOpen, onClose }) => {
  if (!isOpen) return null;

  /* SERVICES – compact 2-col editorial */
  const renderServicesMenu = () => {
    const links = [
      { 
        name: 'Enterprise Software Solutions', 
        path: '/services/enterprise-software-solutions',
        icon: <Building2 size={16} />,
        desc: 'ERP, CRM, HRMS and workflow management systems'
      },
      { 
        name: 'Industrial Automation Service', 
        path: '/services/industrial-software-automation',
        icon: <Cog size={16} />,
        desc: 'Manufacturing automation and IoT integration'
      },
      { 
        name: 'Custom Product Development', 
        path: '/services/custom-product-development',
        icon: <Wrench size={16} />,
        desc: 'Tailored software solutions and product development'
      },
      { 
        name: 'Cloud & IT Services', 
        path: '/services/cloud-it-services',
        icon: <Cloud size={16} />,
        desc: 'Cloud infrastructure and IT management services'
      },
    ];
    const half = Math.ceil(links.length / 2);
    const cols = [links.slice(0, half), links.slice(half)];

    return (
      <div className="smm-panel smm-panel--compact">
        <div className="smm-columns">
          {cols.map((col, ci) => (
            <React.Fragment key={ci}>
              <div className="smm-col">

                <ul className="smm-link-list">
                  {col.map((service, li) => (
                    <li key={li}>
                      <Link to={service.path} className="smm-link smm-link--with-sub" onClick={onClose}>

                        <span>
                          <span className="smm-link-name">
                            {service.icon}
                            {service.name}
                          </span>
                          <span className="smm-link-sub">{service.desc}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              {ci < cols.length - 1 && (
                <div className="smm-divider" aria-hidden="true" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  /* SOLUTIONS – enhanced style with icons and descriptions like Services */
  const renderSolutionsMenu = () => {
    const solutions = [
      { 
        name: 'AI Business Solutions', 
        desc: 'Cognitive decision models and predictive analytics',
        icon: <Brain size={16} />,
        path: '/solutions/ai-business-solutions'
      },
      { 
        name: 'Enterprise Digital Transformation', 
        desc: 'Modernize legacy systems into cloud-native platforms',
        icon: <RefreshCw size={16} />,
        path: '/solutions/enterprise-digital-transformation'
      },
      { 
        name: 'Intelligent Automation Systems', 
        desc: 'Robotic agents and smart workflow triggers',
        icon: <Zap size={16} />,
        path: '/solutions/intelligent-automation-systems'
      },
      { 
        name: 'Industrial IoT Solutions', 
        desc: 'Real-time machine telemetry and edge networks',
        icon: <Factory size={16} />,
        path: '/solutions/industrial-iot-solutions'
      },
      { 
        name: 'Enterprise Workflow Platforms', 
        desc: 'Multi-department task and approval coordination',
        icon: <Workflow size={16} />,
        path: '/solutions/enterprise-workflow-platforms'
      },
      { 
        name: 'Business Process Automation', 
        desc: 'Automate invoicing, onboarding, and data syncs',
        icon: <ClipboardList size={16} />,
        path: '/solutions/business-process-automation'
      },
      { 
        name: 'Cloud-Based Systems', 
        desc: 'Secure cloud migrations and high-availability hosting',
        icon: <Cloud size={16} />,
        path: '/solutions/cloud-based-systems'
      },
      { 
        name: 'Scalable Enterprise Applications', 
        desc: 'Heavy-load portals and bespoke corporate software',
        icon: <Building2 size={16} />,
        path: '/solutions/scalable-enterprise-applications'
      },
    ];

    const half = Math.ceil(solutions.length / 2);
    const cols = [solutions.slice(0, half), solutions.slice(half)];

    return (
      <div className="smm-panel smm-panel--compact">
        <div className="smm-columns">
          {cols.map((col, ci) => (
            <React.Fragment key={ci}>
              <div className="smm-col">

                <ul className="smm-link-list">
                  {col.map((solution, li) => (
                    <li key={li}>
                      <Link to={solution.path} className="smm-link smm-link--with-sub" onClick={onClose}>

                        <span>
                          <span className="smm-link-name">
                            {solution.icon}
                            {solution.name}
                          </span>
                          <span className="smm-link-sub">{solution.desc}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              {ci < cols.length - 1 && (
                <div className="smm-divider" aria-hidden="true" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  /* PRODUCTS – horizontal 3-column layout like Services */
  const renderProductsMenu = () => {
    const products = [
      { 
        name: 'SecurAI', 
        desc: 'Safety, Security & Compliance Suite',
        icon: <Shield size={16} />,
        path: '/products/securai'
      },
      { 
        name: 'FactorAI', 
        desc: 'Intelligent Manufacturing Platform',
        icon: <Cog size={16} />,
        path: '/products/factorai'
      },
      { 
        name: 'SmilAI', 
        desc: 'Intelligent Dentistry & Diagnostic Suite',
        icon: <HeartHandshake size={16} />,
        path: '/products/smilai'
      },
    ];

    return (
      <div className="smm-panel smm-panel--compact">
        <div className="smm-columns">
          {products.map((product, i) => (
            <React.Fragment key={i}>
              <div className="smm-col">

                <ul className="smm-link-list">
                  <li>
                    <Link to={product.path} className="smm-link smm-link--with-sub" onClick={onClose}>

                      <span>
                        <span className="smm-link-name">
                          {product.icon}
                          {product.name}
                        </span>
                        <span className="smm-link-sub">{product.desc}</span>
                      </span>
                    </Link>
                  </li>
                </ul>
              </div>
              {i < products.length - 1 && (
                <div className="smm-divider" aria-hidden="true" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  /* INDUSTRIES � 3-col editorial */
  const renderIndustriesMenu = () => {
    const all = [
      'Manufacturing', 'Airports', 'Ports & Logistics',
      'Healthcare', 'Education', 'Retail',
      'Finance', 'Real Estate', 'Construction',
      'Warehousing', 'Utilities', 'Enterprise Campuses',
    ];
    const size = 4;
    const cols = [all.slice(0, size), all.slice(size, size * 2), all.slice(size * 2)];

    return (
      <div className="smm-panel smm-panel--compact">
        <div className="smm-columns">
          {cols.map((col, ci) => (
            <React.Fragment key={ci}>
              <div className="smm-col">

                <ul className="smm-link-list">
                  {col.map((name, li) => (
                    <li key={li}>
                      <Link to="/industries" className="smm-link" onClick={onClose}>

                        {name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              {ci < cols.length - 1 && (
                <div className="smm-divider" aria-hidden="true" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="smm-wrapper">
      {type === 'services'   && renderServicesMenu()}
      {type === 'solutions'  && renderSolutionsMenu()}
      {type === 'products'   && renderProductsMenu()}
      {type === 'industries' && renderIndustriesMenu()}
    </div>
  );
};

export default MegaMenu;
